import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react'
import { useAuth } from '../contexts/AuthContext'
import ForgotPassword from './ForgotPassword'

interface AuthModalProps {
  isOpen: boolean
  onClose: () => void
}

const AuthModal: React.FC<AuthModalProps> = ({ isOpen, onClose }) => {
  const [isLogin, setIsLogin] = useState(true)
  const [showForgotPassword, setShowForgotPassword] = useState(false)
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [touched, setTouched] = useState<{ email: boolean; password: boolean }>({ email: false, password: false })
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null)

  const turnstileRef = useRef<HTMLDivElement>(null)
  const turnstileWidgetId = useRef<string | null>(null)

  const isLocalDevelopment = useMemo(() => {
    return process.env.NODE_ENV === 'development' ||
        (typeof window !== 'undefined' &&
            (window.location.hostname === 'localhost' ||
                window.location.hostname === '127.0.0.1' ||
                window.location.hostname === '0.0.0.0'))
  }, [])

  const turnstileSiteKey = useMemo(() => {
    if (isLocalDevelopment) {
      // Cloudflare provides test keys for development
      // '1x00000000000000000000AA' always passes
      // '2x00000000000000000000AB' always blocks
      return '1x00000000000000000000AA' // Use test key in dev
    }
    return process.env.REACT_APP_TURNSTILE_SITE_KEY || '0x4AAAAAACDVfk4AfySTotSW'
  }, [isLocalDevelopment])

  const turnstileRequired = !!turnstileSiteKey

  const { login, register } = useAuth()
  // const googleButtonRef = useRef<HTMLDivElement>(null)

  // Load Turnstile script
  useEffect(() => {
    if (!turnstileSiteKey) return

    // Check if script already loaded
    if (document.querySelector('script[src*="turnstile"]')) return

    const script = document.createElement('script')
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
    script.async = true
    script.defer = true
    document.head.appendChild(script)

    return () => {
      // Cleanup if needed
    }
  }, [turnstileSiteKey])

  // Load Google Identity Services script
  // useEffect(() => {
  //   // Check if script already loaded
  //   if (document.querySelector('script[src*="accounts.google.com/gsi/client"]')) return

  //   const script = document.createElement('script')
  //   script.src = 'https://accounts.google.com/gsi/client'
  //   script.async = true
  //   script.defer = true
  //   document.head.appendChild(script)
  // }, [])

  // Render Turnstile widget
  const renderTurnstile = useCallback(() => {
    if (!turnstileRef.current || !turnstileSiteKey) return
    if (!window.turnstile) return

    // Remove existing widget if any
    if (turnstileWidgetId.current) {
      try {
        window.turnstile.remove(turnstileWidgetId.current)
      } catch (e) {
        // Widget might already be removed
      }
      turnstileWidgetId.current = null
    }

    // Clear the container
    turnstileRef.current.innerHTML = ''

    // Render new widget
    turnstileWidgetId.current = window.turnstile.render(turnstileRef.current, {
      sitekey: turnstileSiteKey,
      callback: (token: string) => {
        setTurnstileToken(token)
      },
      'expired-callback': () => {
        setTurnstileToken(null)
      },
      'error-callback': () => {
        setTurnstileToken(null)
        setError('Verification failed. Please try again.')
      },
      theme: 'auto', // or 'light' or 'dark'
    })
  }, [turnstileSiteKey])

  // Initialize/reset Turnstile when modal opens or mode changes
  useEffect(() => {
    if (!isOpen || !turnstileSiteKey) return

    // Wait for script to load
    const checkAndRender = () => {
      if (window.turnstile) {
        renderTurnstile()
      } else {
        // Script not loaded yet, try again
        setTimeout(checkAndRender, 100)
      }
    }

    // Small delay to ensure DOM is ready
    const timeoutId = setTimeout(checkAndRender, 100)

    return () => {
      clearTimeout(timeoutId)
      if (turnstileWidgetId.current && window.turnstile) {
        try {
          window.turnstile.remove(turnstileWidgetId.current)
        } catch (e) {
          // Ignore
        }
        turnstileWidgetId.current = null
      }
    }
  }, [isOpen, isLogin, turnstileSiteKey, renderTurnstile])

  // Initialize Google Sign-In button
  // useEffect(() => {
  //   if (!isOpen || !googleButtonRef.current) return

  //   const initializeGoogleButton = () => {
  //     if (window.google?.accounts?.id) {
  //       window.google.accounts.id.initialize({
  //         client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID || '1064994235876-qm54lj2f7s8g4tqr7l1cqv3m0e5e5q0d.apps.googleusercontent.com',
  //         callback: async (response: any) => {
  //           try {
  //             setLoading(true)
  //             await googleLogin(response.credential)
  //             onClose()
  //             setFormData({ email: '', password: '', firstName: '', lastName: '' })
  //           } catch (err) {
  //             setError(err instanceof Error ? err.message : 'Google sign-in failed')
  //           } finally {
  //             setLoading(false)
  //           }
  //         },
  //       })

  //       if (googleButtonRef.current) {
  //         window.google.accounts.id.renderButton(
  //           googleButtonRef.current,
  //           {
  //             theme: 'outline',
  //             size: 'large',
  //             width: googleButtonRef.current.offsetWidth,
  //             text: isLogin ? 'signin_with' : 'signup_with',
  //           }
  //         )
  //       }
  //     } else {
  //       setTimeout(initializeGoogleButton, 100)
  //     }
  //   }

  //   const timeoutId = setTimeout(initializeGoogleButton, 100)
  //   return () => clearTimeout(timeoutId)
  // }, [isOpen, isLogin, googleLogin, onClose])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
    if (error) setError('')
  }

  const emailValid = useMemo(() => {
    const pattern = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/
    return pattern.test(formData.email.trim())
  }, [formData.email])

  type Strength = 'empty' | 'bad' | 'good' | 'secure'
  const passwordStrength: Strength = useMemo(() => {
    const pw = formData.password
    if (!pw) return 'empty'
    const hasUpper = /[A-Z]/.test(pw)
    const hasLower = /[a-z]/.test(pw)
    const hasDigit = /[0-9]/.test(pw)
    const hasSpecial = /[!@#$%^&*()_+\-={}\[\]\\|:;"'<>,.?/~]/.test(pw)
    const meetsBase = pw.length >= 10 && hasUpper && hasLower && hasDigit && hasSpecial
    if (!meetsBase) return 'bad'
    if (pw.length >= 14) return 'secure'
    return 'good'
  }, [formData.password])

  const strengthLabel = passwordStrength === 'secure' ? 'Secure' : passwordStrength === 'good' ? 'Good' : passwordStrength === 'bad' ? 'Bad' : ''
  const strengthColor = passwordStrength === 'secure' ? 'text-green-600 dark:text-green-400' : passwordStrength === 'good' ? 'text-yellow-600 dark:text-yellow-400' : passwordStrength === 'bad' ? 'text-red-600 dark:text-red-400' : 'text-gray-500'
  const strengthBarClass = passwordStrength === 'secure' ? 'bg-green-500 w-full' : passwordStrength === 'good' ? 'bg-yellow-500 w-2/3' : passwordStrength === 'bad' ? 'bg-red-500 w-1/3' : 'bg-gray-300 w-0'

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      const tokenToSend = turnstileToken || undefined

      if (isLogin) {
        await login(formData.email, formData.password, tokenToSend)
      } else {
        await register(formData.email, formData.password, formData.firstName, formData.lastName, tokenToSend)
      }
      onClose()
      setFormData({ email: '', password: '', firstName: '', lastName: '' })
      setTurnstileToken(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
      // Reset Turnstile on error so user can try again
      if (window.turnstile && turnstileWidgetId.current) {
        window.turnstile.reset(turnstileWidgetId.current)
        setTurnstileToken(null)
      }
    } finally {
      setLoading(false)
    }
  }

  const toggleMode = () => {
    setIsLogin(!isLogin)
    setError('')
    setFormData({ email: '', password: '', firstName: '', lastName: '' })
    setTouched({ email: false, password: false })
    setTurnstileToken(null)
  }

  useEffect(() => {
    if (isOpen) {
      setIsLogin(true)
      setFormData({ email: '', password: '', firstName: '', lastName: '' })
      setTouched({ email: false, password: false })
      setError('')
      setShowPassword(false)
      setTurnstileToken(null)
    }
  }, [isOpen])

  const handleClose = () => {
    setIsLogin(true)
    setFormData({ email: '', password: '', firstName: '', lastName: '' })
    setTouched({ email: false, password: false })
    setError('')
    setShowPassword(false)
    setTurnstileToken(null)
    onClose()
  }

  if (!isOpen) return null

  if (showForgotPassword) {
    return <ForgotPassword onClose={() => setShowForgotPassword(false)} />
  }

  return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
        <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
            onClick={handleClose}
        />

        <div className="relative w-full max-w-md max-h-[90vh] overflow-y-auto bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 transform transition-all my-4">
          {/* Header */}
          <div className="p-6 pb-0">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                {isLogin ? 'Sign In' : 'Create Account'}
              </h2>
              <button
                  onClick={handleClose}
                  className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              {isLogin
                  ? 'Welcome back! Please sign in to your account.'
                  : 'Join us today and start searching for your dream BMW spec.'
              }
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {error && (
                <div className="p-3 text-sm text-red-600 bg-red-50 dark:bg-red-900/20 dark:text-red-400 rounded-lg border border-red-200 dark:border-red-800">
                  {error}
                </div>
            )}

            {!isLogin && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      First Name
                    </label>
                    <input
                        type="text"
                        id="firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        required={!isLogin}
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                        placeholder="First name"
                    />
                  </div>
                  <div>
                    <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Last Name
                    </label>
                    <input
                        type="text"
                        id="lastName"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        required={!isLogin}
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                        placeholder="Last name"
                    />
                  </div>
                </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Email Address
              </label>
              <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  onBlur={() => setTouched(s => ({ ...s, email: true }))}
                  required
                  className={`w-full px-4 py-3 rounded-lg border bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${touched.email && !emailValid ? 'border-red-500 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'}`}
                  placeholder="Enter your email"
              />
              {touched.email && !emailValid && (
                  <p className="mt-1 text-xs text-red-600 dark:text-red-400">Please enter a valid email address (e.g., name@example.com).</p>
              )}
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Password
              </label>
              <div className="relative">
                <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    onBlur={() => setTouched(s => ({ ...s, password: false }))}
                    required
                    minLength={isLogin ? undefined : 10}
                    className={`w-full px-4 py-3 pr-10 rounded-lg border bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${(!isLogin && touched.password && passwordStrength === 'bad') ? 'border-red-500 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'}`}
                    placeholder="Enter your password"
                />
                <button
                    type="button"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    onClick={() => setShowPassword(s => !s)}
                    className="absolute inset-y-0 right-2 px-2 flex items-center text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  {showPassword ? (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-5 0-9-4-9-7 0-1.06.39-2.16 1.125-3.175M6.18 6.18A9.967 9.967 0 0112 5c5 0 9 4 9 7 0 1.06-.39 2.16-1.125 3.175M3 3l18 18M9.88 9.88A3 3 0 0012 15a3 3 0 002.12-5.12"/></svg>
                  ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
                  )}
                </button>
              </div>
              {!isLogin && formData.password && (
                  <div className="mt-2">
                    <div className="h-1.5 w-full bg-gray-200 dark:bg-gray-700 rounded">
                      <div className={`h-1.5 rounded ${strengthBarClass}`}></div>
                    </div>
                    <div className={`mt-1 text-xs ${strengthColor}`}>{strengthLabel || ' '}</div>
                    {passwordStrength === 'bad' && (
                        <div className="mt-1 text-xs text-red-600 dark:text-red-400">
                          Password must be at least 10 characters and include upper, lower, number, and special character.
                        </div>
                    )}
                  </div>
              )}
            </div>

            {isLogin && (
                <div className="flex justify-end">
                  <button
                      type="button"
                      onClick={() => setShowForgotPassword(true)}
                      className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
                  >
                    Forgot password?
                  </button>
                </div>
            )}

            {/* Turnstile Widget */}
            {turnstileSiteKey && (
                <div className="flex justify-center">
                  <div ref={turnstileRef} />
                </div>
            )}

            <button
                type="submit"
                disabled={
                    loading ||
                    (turnstileRequired && !turnstileToken) ||
                    (!isLogin && (!emailValid || passwordStrength === 'bad' || !formData.firstName || !formData.lastName))
                }
                className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
            >
              {loading ? (
                  <div className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {isLogin ? 'Signing in...' : 'Creating account...'}
                  </div>
              ) : (
                  isLogin ? 'Sign In' : 'Create Account'
              )}
            </button>

            {/* Divider */}
            {/* <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300 dark:border-gray-600"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white dark:bg-gray-900 text-gray-500 dark:text-gray-400">Or continue with</span>
              </div>
            </div> */}

            {/* Google Sign-In Button */}
            {/* <div ref={googleButtonRef} className="w-full flex justify-center"></div> */}
          </form>

          {/* Footer */}
          <div className="px-6 pb-6">
            <div className="text-center">
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {isLogin ? "Don't have an account?" : "Already have an account?"}
            </span>
              <button
                  onClick={toggleMode}
                  className="ml-1 text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
              >
                {isLogin ? 'Create one' : 'Sign in'}
              </button>
            </div>
          </div>
        </div>
      </div>
  )
}

declare global {
  interface Window {
    turnstile: {
      render: (container: HTMLElement, options: {
        sitekey: string
        callback: (token: string) => void
        'expired-callback'?: () => void
        'error-callback'?: () => void
        theme?: 'light' | 'dark' | 'auto'
      }) => string
      reset: (widgetId: string) => void
      remove: (widgetId: string) => void
    }
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string
            callback: (response: { credential: string }) => void
          }) => void
          renderButton: (
            parent: HTMLElement,
            options: {
              theme?: 'outline' | 'filled_blue' | 'filled_black'
              size?: 'large' | 'medium' | 'small'
              width?: number
              text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin'
            }
          ) => void
        }
      }
    }
  }
}

export default AuthModal